<template>
  <td class="row-column" v-if="collapsibleSelectionEnabled"></td>
  <td :colspan="columnsContentCount" class="row-column">
    <hcl-list
      :definition="listDefinition"
      :actionlistid="parentActionListId"
      :embedded="true"
      v-on:hcl-list-embedded-updated="onHclListEmbeddedUpdated"
    ></hcl-list>
  </td>
  <td v-if="collapsibleButtonShow" class="row-column">
    <div class="wrapper">
      <div class="column-collapsible">
        <a type="button" class="btn float-right" @click.prevent="collapsibleButtonOnClicked(props.id, props.index)"
          :title="collapsibleButtonStateIsOpen ? collapsibleButtonStateIsOpenMessage : collapsibleButtonStateIsClosedMessage">
          <i v-if="collapsibleButtonStateIsOpen" class="eii--fa6-solid--square-minus"></i>
          <i v-if="!collapsibleButtonStateIsOpen" class="eii--fa6-solid--square-plus"></i>
        </a>
      </div>
    </div>
  </td>
</template>

<script setup lang="ts">
import { ref, computed, watch, inject, provide } from 'vue'

import { DefinitionListContentColumn, DataListRowEntityEntry, DefinitionList, DefinitionListContentConfiguration, DataListLookup, DefinitionListContentConfigurationSelection } from '@/components/types'

import { useDataStore } from '@/stores/data'

import HclList from '@/components/List/List.vue'

import { useCollapsible } from '@/components/ListRowColumnBody/collapsible'

interface Props {
  columns: DefinitionListContentColumn[]
  config: DefinitionListContentConfiguration
  lookups: DataListLookup[]
  entry: DataListRowEntityEntry
  selection: DefinitionListContentConfigurationSelection
  id: string | number
  index: string | number
  columncount: number,
  entity: string
}

const props = withDefaults(defineProps<Props>(), {
  columns: undefined,
  config: undefined,
  lookups: undefined,
  entry: undefined,
  selection: undefined,
  id: undefined,
  index: undefined,
  columncount: undefined,
  entity: undefined
})

// @improvement: See if we can implement a loading bar on the sub-list during fetching of data

const listMetaId: string = inject('listMetaId', '')

const config = ref(props.config)

const listDefinition = computed((): DefinitionList => {
  return {
    meta: {
      id: listMetaId + '/' + props.entity + '/' + props.id,
      name: listMetaId + '/' + props.entity + '/' + props.id,
      type: 'list',
      version: 1,
    },
    header: [],
    content: [
      {
        name: '',
        type: 'columns',
        entity: '',
        columns: props.columns,
        configuration: config.value
      }
    ],
    footer: []
  }
})

const parentActionListId = ref(props.entry.state.actionlist?.id ?? '0')

watch(
  props,
  (n) => {
    parentActionListId.value = n.entry.state.actionlist?.id ?? '0'
  }
)

provide('parentActionListId', parentActionListId)

const emit = defineEmits(['hcl-list-row-column-body-collapsible-clicked', 'hcl-list-embedded-updated'])

const onHclListEmbeddedUpdated = () => {
  emit('hcl-list-embedded-updated')
}

const collapsibleButtonOnClicked = (id: string | number, index: string | number) => {
  emit('hcl-list-row-column-body-collapsible-clicked', { id: id, index: index })
}

const {
  collapsibleButtonShow,
  collapsibleButtonStateIsOpen,
  collapsibleButtonStateIsOpenMessage,
  collapsibleButtonStateIsClosedMessage,
  collapsibleSelectionEnabled,
  columnsContentCount
} = useCollapsible(props)

const { link } = useDataStore()

const bucket = link(listMetaId + '/' + props.entity + '/' + props.id)

watch(
  bucket,
  (n) => {
    if ('selection' in n) {
      config.value.selection = n.selection
    }
  }
)
</script>

<style lang="scss" scoped>
.row-column {
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
}
</style>
